import { useState, useEffect } from "react";
import { trackEvent } from "@utils/analytics";

// We use a container ID instead of a video ID so we can have a unique reference
// to each player instance. We can't rely on the wistia video ID because we
// sometimes need to use the same video in multiple components on the same page
// but we want to be able to e.g. track analytics for these independently.
export const useWistiaPlayer = ({ wistiaContainerId, trackedEventName }) => {
  if (!wistiaContainerId) {
    console.warn("useWistiaPlayer : Missing required wistiaContainerId");
    return null;
  }

  const [wistiaPlayer, setWistiaPlayer] = useState(null);

  // Initialize the wistia player
  useEffect(() => {
    window._wq = window._wq ?? [];
    _wq.push({
      id: wistiaContainerId,
      onReady: (player) => {
        setWistiaPlayer(player);
      },
    });
  }, []);

  // Setup event listeners
  useEffect(() => {
    if (wistiaPlayer && wistiaPlayer.ready()) {
      if (trackedEventName) {
        wistiaPlayer.bind("play", trackFirstPlay);
        wistiaPlayer.bind("percentwatchedchanged", trackCompletedVideo);
      }
    }
  }, [wistiaPlayer]);

  const trackFirstPlay = () => {
    trackEvent(`${trackedEventName} Started`);
    wistiaPlayer.unbind("play", trackFirstPlay);
  };

  const trackCompletedVideo = (() => {
    let hasCompletedVideo = false;

    // Intentional closure to ensure event only gets emitted once
    return (percent, lastPercent) => {
      if (percent >= 0.8 && lastPercent < 0.8 && !hasCompletedVideo) {
        hasCompletedVideo = true;
        trackEvent(`${trackedEventName} 80% Watched`);
        wistiaPlayer.unbind("percentwatchedchanged", trackCompletedVideo);
      }
    };
  })();

  return Object.assign([wistiaPlayer], {
    wistiaPlayer,
  });
};

export default useWistiaPlayer;
